<template>
  <div class="mt-3">
    <!--For CLipart layers-->
    <p class="title is-6 has-text-centered">Number Line <b-button @click="toggle()" type="is-success" size="is-small">Toggle</b-button></p>
    
    <div v-if="number_line.show" class="columns is-multiline">

      <div class="column is-3">
        <b-field label="Canvas Width">
          <b-numberinput v-model="number_line.canvas_width"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Canvas Height">
          <b-numberinput v-model="number_line.canvas_height"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="X-Axis Margin">
          <b-numberinput v-model="number_line.x_margin"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Use Fraction Labels">
          <b-switch v-model="number_line.use_fractions"></b-switch>
        </b-field>
      </div>

      <!-- Regular number mode -->
      <template v-if="!number_line.use_fractions">
        <div class="column is-3">
          <b-field label="Start Number">
            <b-numberinput v-model="number_line.start_num" :step="0.1"></b-numberinput>
          </b-field>
        </div>
   
        <div class="column is-3">
          <b-field label="End Number">
            <b-numberinput v-model="number_line.end_num" :step="0.1"></b-numberinput>
          </b-field>
        </div>

        <div class="column is-3">
          <b-field label="Step">
            <b-numberinput v-model="number_line.step" :step="0.1"></b-numberinput>
          </b-field>
        </div>
      </template>

      <!-- Fraction mode - custom labels -->
      <template v-if="number_line.use_fractions">
        <div class="column is-12">
          <p class="title is-6">Fraction Labels <b-button @click="addFractionLabel()" type="is-success" size="is-small">Add</b-button></p>
          <p class="subtitle is-7 has-text-grey">Enter labels like "1/2", "2 1/3", or regular numbers. These will be displayed on the number line.</p>
          <div class="columns is-multiline">
            <div v-for="(label, i) in number_line.fraction_labels" :key="`FRACTION_LABEL_${i}`" class="column is-2">
              <b-field>
                <b-input v-model="number_line.fraction_labels[i]" placeholder="e.g. 1/2"></b-input>
                <span class="control"><b-button @click="removeFractionLabel(i)" type="is-danger" icon-left="close"></b-button></span>
              </b-field>
            </div>
          </div>
        </div>
      </template>

      <div class="column is-3">
        <b-field label="Line Stroke Weight">
          <b-numberinput v-model="number_line.stroke_weight"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Text Size">
          <b-numberinput v-model="number_line.text_size"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Line Color">
          <b-input v-model="number_line.line_color"></b-input>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Text Color">
          <b-input v-model="number_line.text_color"></b-input>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Input Width">
          <b-numberinput v-model="number_line.input_width"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Input Height">
          <b-numberinput v-model="number_line.input_height"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Input Border Weight">
          <b-numberinput v-model="number_line.input_border_weight"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Input Border Color">
          <b-input v-model="number_line.input_border_color"></b-input>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Input Margin (From Center)">
          <b-numberinput v-model="number_line.input_top_margin"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Text Margin (From Center)">
          <b-numberinput v-model="number_line.text_top_margin"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Make Number Selectable">
          <b-switch v-model="number_line.allow_select"></b-switch>
        </b-field>
      </div>

      <!-- Inputs Section - Different UI for fraction mode -->
      <div class="column is-12">
        <p class="title is-6">Inputs (Replace label with input box) <b-button @click="addInputToNumberLine()" type="is-success" size="is-small">Add</b-button></p>
        <p class="subtitle is-7 has-text-grey" v-if="number_line.use_fractions">Select which fraction labels should be replaced with input boxes</p>
        <div v-for="(input_val, i) in number_line.inputs" :key="`NUMBERLINE_INP_${i}`" class="mx-2" style="width: 150px;float:left">
          <b-field>
            <!-- In fraction mode, use select dropdown to pick from fraction_labels -->
            <b-select v-if="number_line.use_fractions" v-model="number_line.inputs[i]" placeholder="Select label">
              <option v-for="(label, li) in number_line.fraction_labels" :key="`inp_opt_${li}`" :value="label">{{ label }}</option>
            </b-select>
            <!-- In regular mode, use number input -->
            <b-numberinput v-else :controls="false" :step="0.01" v-model="number_line.inputs[i]"></b-numberinput>
            <span class="control"><b-button @click="removeInputFromNumberLine(i)" type="is-danger" icon-left="close"></b-button></span>
          </b-field>
        </div>
        <div style="clear:both"></div>

        <div v-if="number_line.inputs && number_line.inputs.length" class="mt-3">
          <b-checkbox v-model="number_line.answer_not_exactly_same">
            Answer Not Exactly Same
          </b-checkbox>
          <p class="help has-text-grey">
            When enabled, the learner's answer is compared ignoring
            whitespace and letter casing (e.g. "1 / 2" matches "1/2").
          </p>
        </div>
      </div>

      <!--Arrow settings-->
      <div class="column is-12">
        <p class="title is-5">Dealing with arrows <b-button @click="addArrowToNumberLine()" type="is-success" size="is-small">Add</b-button></p>
        <p class="subtitle is-7 has-text-grey" v-if="number_line.use_fractions">In fraction mode: "From" selects the starting label, "Step" is how many labels to jump</p>
        
        <b-table v-if="number_line.arrows && number_line.arrows.length" :data="number_line.arrows">
          <b-table-column v-slot="props" field="from" label="From">
            <!-- In fraction mode, use select dropdown -->
            <b-select v-if="number_line.use_fractions" v-model="props.row.from" placeholder="Select label">
              <option v-for="(label, li) in number_line.fraction_labels" :key="`arrow_from_${li}`" :value="label">{{ label }}</option>
            </b-select>
            <!-- In regular mode, use number input -->
            <b-numberinput v-else :controls="false" :step="0.01" v-model="props.row.from"></b-numberinput>
          </b-table-column>
          <b-table-column v-slot="props" field="step" label="Step (# of labels)">
            <b-numberinput :controls="false" :step="1" v-model="props.row.step"></b-numberinput>
          </b-table-column>
          <b-table-column v-slot="props" field="thickness" label="Thickness">
            <b-numberinput :controls="false" v-model="props.row.thickness"></b-numberinput>
          </b-table-column>
          <b-table-column v-slot="props" field="color" label="Color">
            <b-input v-model="props.row.color"></b-input>
          </b-table-column>
          <b-table-column v-slot="props" field="show_arrow" label="Arrow State">
            <b-select v-model="props.row.show_arrow">
              <option :value="true">Show Arrow</option>
              <option :value="false">Hide Arrow</option>
            </b-select>
          </b-table-column>
          <b-table-column v-slot="props" field="show_step" label="Show Step">
            <b-switch type="is-success" v-model="props.row.show_step"></b-switch>
          </b-table-column>
          <b-table-column v-slot="props" field="action" label="Action">
            <b-button @click="removeArrowFromNumberLine(props.row.id)" type="is-danger" icon-left="close"></b-button>
          </b-table-column>
        </b-table>  
      </div>

      <!--Number settings (highlight, select answer) -->
      <div class="column is-12">
        <p class="title is-5">Number Settings <b-button @click="addNumberRow()" type="is-success" size="is-small">Add</b-button></p>
        <p class="subtitle is-7 has-text-grey" v-if="number_line.use_fractions">Configure highlighting and answer selection for specific labels</p>
        
        <b-table v-if="number_line.numbers && number_line.numbers.length" :data="number_line.numbers">
          <b-table-column v-slot="props" field="number" label="Label">
            <!-- In fraction mode, use select dropdown -->
            <b-select v-if="number_line.use_fractions" v-model="props.row.number" placeholder="Select label">
              <option v-for="(label, li) in number_line.fraction_labels" :key="`num_opt_${li}`" :value="label">{{ label }}</option>
            </b-select>
            <!-- In regular mode, use number input -->
            <b-numberinput v-else :controls="false" :step="0.01" v-model="props.row.number"></b-numberinput>
          </b-table-column>
          
          <b-table-column v-slot="props" field="highlight" label="Highlight">
            <b-switch type="is-success" v-model="props.row.is_highlight"></b-switch>
          </b-table-column>

          <b-table-column v-slot="props" field="highlight_color" label="Highlight Color">
            <b-input v-model="props.row.highlight_color"></b-input>
          </b-table-column>

          <b-table-column v-slot="props" field="is_select_answer" label="Is Select Answer">
            <b-switch type="is-success" v-model="props.row.is_select_answer"></b-switch>
          </b-table-column>

          <b-table-column v-slot="props" field="action" label="Action">
            <b-button @click="removeNumberRow(props.row.id)" type="is-danger" icon-left="close"></b-button>
          </b-table-column>
        </b-table>  
      </div>
      
      <div class="column is-12">
        <p class="title is-6">View</p>
        <show-number-line :number_line="number_line"></show-number-line>
      </div>
    </div>

  </div>
</template>
<script>
import {mapGetters} from 'vuex'
import {defaultNumberLineValue} from '@/components/Question/NumberLine/utils'
import ShowNumberLine from '@/components/Question/NumberLine/ShowNumberLine'

export default {
  name : 'set-number-line',
  props : {
    value : {
      type : Object
    }, // Here value = new_question.number_line (in parent component)
  }, 
  components : {
    ShowNumberLine,
  },
  data(){
    return {
      number_line : this.value ? this.value : defaultNumberLineValue(),
      inputs : ['']
    }
  },
  computed : {
    ...mapGetters(['']),
  },
  methods : {
    toggle(){
      this.$set(this.number_line, 'show', !this.number_line.show)
    },
    emitData(){
      this.$emit('input', this.number_line)
    },
    addInputToNumberLine(){
      // In fraction mode, push empty string to select from dropdown
      // In regular mode, push 0
      const defaultValue = this.number_line.use_fractions ? '' : 0
      this.number_line.inputs.push(defaultValue)
    },
    addArrowToNumberLine(){
      // In fraction mode, from should be a string (label), step is still a number (how many labels to jump)
      const defaultFrom = this.number_line.use_fractions ? '' : 0
      this.number_line.arrows.push({
        from : defaultFrom,
        step : 1,
        show_arrow : false,
        thickness : 1,
        color : '#000',
        show_step : true
      })
    },
    addNumberRow(){
      // In fraction mode, number should be a string (label)
      const defaultNumber = this.number_line.use_fractions ? '' : 0
      this.number_line.numbers.push({
        number : defaultNumber,
        is_highlight : false,
        highlight_color : '#000',
        is_select_answer : false
      })
    },
    removeNumberRow(i){
      this.number_line.numbers.splice(i, 1)
    },
    removeInputFromNumberLine(i){
      this.number_line.inputs.splice(i, 1)
    },
    removeArrowFromNumberLine(i){
      this.number_line.arrows.splice(i, 1)
    },
    addFractionLabel(){
      if(!this.number_line.fraction_labels){
        this.$set(this.number_line, 'fraction_labels', [])
      }
      this.number_line.fraction_labels.push('')
    },
    removeFractionLabel(i){
      this.number_line.fraction_labels.splice(i, 1)
    }
    
  },
  watch : {
    number_line : {
      handler(){
        this.emitData()
      },
      deep : true
    },
    value : {
      handler(new_data){
        this.number_line = new_data
      },
      deep : true
    }
  }
}
</script>
